import { Lock, CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * Single field-status row in the candidate sidebar list (desktop) or
 * mobile field-sheet. Tapping it jumps the PDF to that field's page
 * and focuses it. Status pip on the right reflects:
 *   - locked / auto-fill-on-completion → amber Lock
 *   - complete → green check
 *   - required + empty → orange alert
 *   - optional + empty → grey hollow circle
 */
const FieldRow = ({ field, value, isActive, onClick, density }) => {
  const isAutoCompletion = field.field_type === 'date' && field.auto_fill_completion_date;
  const isComplete = isAutoCompletion
    ? true
    : (field.field_type === 'checkbox' ? value === true : value && String(value).trim() !== '');
  const isLocked = field.candidate_editable === false || isAutoCompletion;

  const padding = density === 'compact' ? 'p-2.5' : 'p-3';
  const hoverWhenComplete = density === 'compact' ? 'hover:bg-green-100' : '';

  return (
    <div
      onClick={onClick}
      className={`${padding} rounded-lg border cursor-pointer transition-all text-sm ${
        isActive
          ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
          : isLocked
            ? 'border-amber-200 bg-amber-50'
            : isComplete
              ? `border-green-200 bg-green-50 ${hoverWhenComplete}`
              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{field.label}</p>
          <p className="text-xs text-muted-foreground">
            Page {field.page || 1}
            {field.required && !isLocked && <span className="text-red-500 ml-1">*</span>}
          </p>
        </div>
        {isLocked ? (
          <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
        ) : isComplete ? (
          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
        ) : field.required ? (
          <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
        ) : (
          <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex-shrink-0" />
        )}
      </div>
    </div>
  );
};

/**
 * Scrollable list of fields with their completion status. Used in
 * both the desktop sidebar (`density="compact"`) and the mobile
 * bottom-sheet (`density="comfortable"`).
 */
export const FieldStatusList = ({
  fields,
  fieldValues,
  activeFieldId,
  isConditionMet,
  onSelectField,
  density = 'compact',
  containerClass = 'flex-1 overflow-auto p-3 space-y-2',
}) => (
  <div className={containerClass}>
    {fields.filter(f => isConditionMet(f)).map((field) => (
      <FieldRow
        key={field.id}
        field={field}
        value={fieldValues[field.id]}
        isActive={activeFieldId === field.id}
        onClick={() => onSelectField(field)}
        density={density}
      />
    ))}
  </div>
);
